<script lang="ts">
    // Jet/Svelte
    import { createEventDispatcher, onMount } from 'svelte';

    // Utilities
    import { getPlayback } from '~/utils/playback';

    // Stores
    import {
        NowPlayingView,
        fullScreenPlayerState,
    } from '~/stores/full-screen-player';
    import { getI18n } from '~/stores/i18n';
    import { queueMetadata } from '~/stores/queue-metadata';

    // Components
    import FallbackArtwork from '~/components/jet/Artwork/fallbacks/classical-symbol-square.svelte';
    import NowPlayingMetadata from '~/components/structure/Players/components/NowPlayingMetadata.svelte';
    import NowPlayingInfo from '~/components/structure/Players/components/NowPlayingInfo/NowPlayingInfo.svelte';
    import NowPlayingQueue from '~/components/structure/Players/components/NowPlayingQueue/NowPlayingQueue.svelte';
    import ViewToggle from '~/components/structure/Players/components/ViewToggle.svelte';

    // Shared Components
    import SharedDesktopNowPlaying from '@amp/web-app-components/src/components/DesktopNowPlaying/DesktopNowPlaying.svelte';
    import PlaybackControls from '@amp/web-app-components/src/components/PlaybackControls/PlaybackControls.svelte';
    import InvertibleMask from '@amp/web-app-components/src/components/InvertibleMask/InvertibleMask.svelte';
    import UpNextIcon from '@amp/web-app-components/assets/icons/up-next-semibold.svg';
    import Artwork from '@amp/web-app-components/src/components/Artwork/Artwork.svelte';
    import { platform } from '@amp/web-apps-utils';
    import { buildSrc } from '@amp/web-app-components/src/components/Artwork/utils/srcset';

    const i18n = getI18n();
    const { queue } = getPlayback();
    const isiOS = platform.isiOS();

    // props for shared component
    export let modalTriggerElement: HTMLElement;
    export let isFullScreenActive: boolean;
    export let ariaLabel: string;
    export let translateFn: (
        str: string,
        values?: Record<string, string | number>,
    ) => string;

    const dispatch = createEventDispatcher();

    // Updates contextual menu config to render the <amp-contextual-menu />
    // element somewhere other than the default of document.body
    let parentElement: HTMLElement;

    $: nowPlayingItemId = $queue.nowPlayingItemInfo?.id;
    $: item = !!nowPlayingItemId && $queueMetadata?.get(nowPlayingItemId);
    $: artwork = item?.artwork;

    $: artworkURL = (() => {
        return buildSrc(
            artwork?.template,
            {
                width: 40,
                height: 40,
                crop: 'bb',
                fileType: 'webp',
            },
            {},
        );
    })();

    $: queueButtonAriaLabel = $i18n.t('ASE.Web.MusicClassical.UpNext.Heading');

    $: nowPlayingView = $fullScreenPlayerState.nowPlayingView;
    $: isQueueView = nowPlayingView === NowPlayingView.Queue;
    $: isInfoView = nowPlayingView === NowPlayingView.Info;
    $: isArtworkView = nowPlayingView === NowPlayingView.Artwork;

    const toggleView = (view: NowPlayingView) => {
        dispatch('toggleView', view);
    };

    onMount(() => {
        parentElement = document.querySelector('#modal-content-wrapper');
    });
</script>

<SharedDesktopNowPlaying
    {modalTriggerElement}
    {isFullScreenActive}
    isTextVisible={isInfoView || isQueueView}
    {artworkURL}
    {ariaLabel}
    {translateFn}
    on:close
>
    <svelte:fragment slot="artwork-radiosity">
        <Artwork
            {artwork}
            useContainerStyle={false}
            profile="track-list"
            alt=""
        />
    </svelte:fragment>
    <svelte:fragment slot="artwork">
        <Artwork
            {artwork}
            useContainerStyle={false}
            disableAutoCenter={true}
            profile="info-fullscreen"
            alt=""
        >
            <svelte:fragment slot="placeholder-component">
                {#if FallbackArtwork}
                    <div
                        class="artwork-fallback"
                        data-testid="artwork-fallback"
                    >
                        <svelte:component this={FallbackArtwork} />
                    </div>
                {/if}
            </svelte:fragment>
        </Artwork>
    </svelte:fragment>

    <svelte:fragment slot="metadata">
        <NowPlayingMetadata {item} bind:parentElement />
    </svelte:fragment>

    <svelte:fragment slot="playback-controls">
        <div class="playback-controls-progress">
            <amp-playback-controls-progress data-theme="video" />
        </div>
        <PlaybackControls realm="classical" isNowPlaying={true} />
        {#if !isiOS}
            <amp-chrome-volume />
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="text">
        {#if isInfoView}
            <NowPlayingInfo isMobileView={false} componentType={'panel'} />
        {:else if isQueueView}
            <NowPlayingQueue
                isMobileView={false}
                componentType={'panel'}
                on:close
            />
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="now-playing-toggle">
        <div class="now-playing-toggle">
            <ViewToggle on:toggleView />
        </div>
    </svelte:fragment>
</SharedDesktopNowPlaying>

<style lang="scss">
    @use '@amp/web-shared-styles/app/core/globalvars' as *;

    .artwork-fallback {
        max-width: 100%;
        max-height: 100%;
        background-color: var(--genericJoeColor);

        &,
        :global(svg) {
            width: inherit;
            height: inherit;
        }
    }

    .playback-controls-progress {
        --progress-track-color-elapsed: #{rgb(255, 255, 255)};
        --progress-track-color: var(--systemSecondary-onDark);
        padding: 20px 0 0;
        width: 100%;

        amp-playback-controls-progress {
            --progress-track-height: #{$playback-progress-track-size};
            --progress-thumb-width: #{$playback-progress-thumb-size};
            --progress-thumb-height: #{$playback-progress-thumb-size};
            --progress-thumb-offset: -#{$playback-progress-track-size-offset};
        }

        @media (prefers-contrast: more) {
            --progress-track-color-elapsed: var(--systemPrimary);
            --progress-track-color: var(--systemTertiary);
            --progress-track-color-active: var(--systemPrimary);
            --progress-track-color-elapsed-active: var(--systemPrimary);
        }
    }

    :global(.desktop-now-playing__container .player) {
        grid-template:
            'artwork' auto
            'metadata' 69px
            'scrubber' 65px
            'controls' 55px
            'volume' 26px;

        amp-chrome-volume {
            --progress-track-height: #{$playback-progress-track-size};
            --progress-thumb-width: #{$playback-progress-thumb-size};
            --progress-thumb-height: #{$playback-progress-thumb-size};
            --chromeVolumeWidth: auto;
            --chromeVolumeIcon: var(--systemPrimary-onDark);
            --chromeVolumeTrack: var(--systemTertiary-onDark);
            grid-template-columns: auto 1fr;
            width: 100%;

            @media (prefers-contrast: more) {
                --chromeVolumeIcon: var(--systemPrimary);
                --chromeVolumeTrack: var(--systemTertiary);
            }
        }

        :global(.playback-controls) {
            --playback-control-button-width: #{$playback-control-button-spacing-desktop}; // 65
            --playback-control-icon-width: #{$playback-control-icon-primary-size}; // 55
            --playback-control-icon-height: #{$playback-control-icon-primary-size}; // 55
            --skip-icon-width: #{$playback-control-icon-secondary-size}; // 50
            --skip-icon-height: #{$playback-control-icon-secondary-size}; // 50
            --skip-control-color-hover: rgb(255, 255, 255);
            --skip-control-color: #{$playback-control-icon-secondary-color};
            padding-bottom: 20px;
        }

        @media (prefers-contrast: more) {
            --playback-controls-play-color: var(--systemPrimary);
            --progress-thumb-color: var(--systemSecondary);
            --progress-thumb-color-active: var(--systemPrimary);
            --chromeVolumeElapsed: var(--systemPrimary);
        }
    }

    :global(.desktop-now-playing__container .text) {
        --fade-mask-height: 80px;
    }
</style>
